import { useMemo } from 'react';
import { Button, Card, Segmented, Skeleton, Tooltip, Typography } from 'antd';
import dayjs from 'dayjs';
import type { TaskActivityItem } from '@crm/shared';
import { useTaskActivity } from '../../features/tasks/api';
import { useProjectScope } from '../../features/projects/scope';
import { PageHeader } from '../../components/PageHeader';
import { ProjectTag } from '../../components/ProjectSelect';
import { EmptyState, LoadError } from '../../components/states';
import { fmtDate, fmtDateTime } from '../../utils/format';
import { ActivityText, UserAvatar } from './TaskBits';
import { useStoredState } from './TaskToolbar';
import './tasks.css';

function dayTitle(iso: string) {
  const d = dayjs(iso);
  if (d.isSame(dayjs(), 'day')) return 'Today';
  if (d.isSame(dayjs().subtract(1, 'day'), 'day')) return 'Yesterday';
  return d.isAfter(dayjs().subtract(6, 'day')) ? d.format('dddd') : fmtDate(iso);
}

/** Recent task activity across the workspace (or the selected project), newest first. */
export function TaskActivityPage() {
  const [who, setWho] = useStoredState<'all' | 'mine'>('crm.tasks.activity.who', 'all');
  const feed = useTaskActivity(who === 'mine');
  const scope = useProjectScope();
  const days = useMemo(() => {
    const out: { title: string; items: TaskActivityItem[] }[] = [];
    for (const item of feed.data?.pages.flatMap((p) => p.items) ?? []) {
      const title = dayTitle(item.createdAt);
      if (out[out.length - 1]?.title !== title) out.push({ title, items: [] });
      out[out.length - 1].items.push(item);
    }
    return out;
  }, [feed.data]);

  return (
    <div className="page">
      <PageHeader
        title="Task activity"
        subtitle={`What changed on tasks${scope.current ? ` in ${scope.current.name}` : ''}: new tasks, completions, comments, files and edits.`}
        extra={
          <Segmented
            value={who}
            onChange={setWho}
            options={[
              { value: 'all', label: 'Everyone’s tasks' },
              { value: 'mine', label: 'Assigned to me' },
            ]}
          />
        }
      />
      <Card>
        {feed.isError ? (
          <LoadError error={feed.error} onRetry={feed.refetch} title="Could not load activity" />
        ) : feed.isLoading ? (
          <Skeleton active avatar paragraph={{ rows: 6 }} />
        ) : !days.length ? (
          <EmptyState description={who === 'mine' ? 'No activity on tasks assigned to you yet.' : 'No task activity yet.'} />
        ) : (
          <>
            {days.map((day) => (
              <section key={day.title} style={{ marginBottom: 16 }}>
                <div className="task-activity-day">{day.title}</div>
                {day.items.map((item) => (
                  <div key={item.id} className="task-activity-item">
                    {item.actor ? <UserAvatar user={item.actor} size={32} /> : <span style={{ width: 32 }} />}
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div>
                        <ActivityText item={item} withTask />
                      </div>
                      {item.type === 'comment' && typeof item.payload.excerpt === 'string' && <div className="task-activity-quote">{item.payload.excerpt}</div>}
                    </div>
                    <ProjectTag projectId={item.task.projectId} />
                    <Tooltip title={fmtDateTime(item.createdAt)}>
                      <Typography.Text type="secondary" style={{ fontSize: 12, whiteSpace: 'nowrap' }}>
                        {dayjs(item.createdAt).format('HH:mm')}
                      </Typography.Text>
                    </Tooltip>
                  </div>
                ))}
              </section>
            ))}
            {feed.hasNextPage && (
              <div style={{ textAlign: 'center' }}>
                <Button onClick={() => void feed.fetchNextPage()} loading={feed.isFetchingNextPage}>
                  Load older activity
                </Button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
